import { Card, CardContent, CardDescription, CardHeader, CardTitle, Skeleton, Input, Button } from "randmarcomps";
import { AlertCircle, Loader2, RefreshCcw, Search, ShieldCheck, Sparkles, Star, Plus } from "lucide-react";
import React from "react";
import { ManufacturerCategory, useGetV4ManufacturerByRouteManufacturerIdCategoriesQuery } from "randmar-api-client";
import CategoryMarginsGrid from "../components/categories/CategoryMarginsGrid";
import CreateCategory from "../components/categories/CreateCategory";
import { DocumentHeader } from "../components/util/StyledComponents";
import { useManufacturer } from "../contexts/WithManufacturer";
import withRoot from "../withRoot";

type FilterOption = "all" | "starred" | "auto" | "requiresQualification";

const Categories: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { data: categoriesData, isLoading: loading } = useGetV4ManufacturerByRouteManufacturerIdCategoriesQuery({ routeManufacturerId: applicationId });
  const categories: ManufacturerCategory[] = React.useMemo(
    () => (categoriesData as ManufacturerCategory[]) ?? [],
    [categoriesData]
  );
  const [searchTerm, setSearchTerm] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<FilterOption>("all");
  const [showFilterSpinner, setShowFilterSpinner] = React.useState(false);

  React.useEffect(() => {
    if (loading) {
      setShowFilterSpinner(false);
      return;
    }
    if (searchTerm.trim() === "" && statusFilter === "all") {
      setShowFilterSpinner(false);
      return;
    }

    setShowFilterSpinner(true);
    const timer = setTimeout(() => setShowFilterSpinner(false), 400);
    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, loading]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredCategories = React.useMemo(() => {
    return categories.filter(category => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        [category.CategoryName, category.CategoryCode, category.Niche]
          .some(value => (value || "").toLowerCase().includes(normalizedSearch));

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "starred" && Boolean(category.Starred)) ||
        (statusFilter === "auto" && Boolean(category.AutoReorder)) ||
        (statusFilter === "requiresQualification" && Boolean(category.QualificationRequired));

      return matchesSearch && matchesStatus;
    });
  }, [categories, normalizedSearch, statusFilter]);

  const stats = React.useMemo(() => [
    { label: "Total categories", value: categories.length.toString() },
    { label: "Auto reorder", value: categories.filter(category => category.AutoReorder).length.toString() },
    { label: "Qualification required", value: categories.filter(category => category.QualificationRequired).length.toString() },
  ], [categories]);

  const filterOptions: { id: FilterOption; label: string; icon: React.ReactNode }[] = [
    { id: "all", label: "All statuses", icon: <Sparkles className="h-4 w-4" /> },
    { id: "starred", label: "Starred", icon: <Star className="h-4 w-4" /> },
    { id: "auto", label: "Auto reorder", icon: <RefreshCcw className="h-4 w-4" /> },
    { id: "requiresQualification", label: "Qualification", icon: <ShieldCheck className="h-4 w-4" /> },
  ];

  return (
    <>
      <DocumentHeader
        sticky={false}
        justifyContent="space-between"
        alignItems="stretch"
        direction="column"
        className="gap-6 border border-[#d8c8f5] bg-gradient-to-r from-[#8E77C8]/20 via-[#f7f2ff] to-white"
      >
        <div className="flex flex-col gap-4">
          <div className="categories-accent-text flex flex-wrap items-center gap-2 text-sm font-medium uppercase tracking-wide">
            <span className="categories-accent-pill inline-flex items-center gap-2 rounded-full px-3 py-1">
              <Sparkles className="h-4 w-4" />
              Manufacturer Portal
            </span>
            {loading && (
              <span className="inline-flex items-center gap-1 categories-accent-text">
                <Loader2 className="h-4 w-4 animate-spin" />
                Syncing
              </span>
            )}
          </div>
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Categories</h1>
            <p className="mt-1 text-base text-slate-600">
              Organize assortments, monitor automation readiness, and keep partners aligned from one modern workspace.
            </p>
          </div>
          <div className="grid gap-3 text-sm sm:grid-cols-3">
            {stats.map(stat => (
              <div
                key={stat.label}
                className="categories-stat-card rounded-xl border bg-white/80 px-4 py-3 shadow-sm"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{stat.label}</p>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-end">
          <CreateCategory categories={categories} />
        </div>
      </DocumentHeader>
      <section className="mb-6 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by name, code, or niche"
              className="w-full rounded-xl border-slate-200 bg-slate-50 pl-9 text-sm"
              aria-label="Search categories"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-[color:var(--ops-accent)]"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {filterOptions.map(option => {
              const isActive = option.id === statusFilter;
              return (
                <button
                  type="button"
                  key={option.id}
                  aria-pressed={isActive}
                  onClick={() => setStatusFilter(option.id)}
                  className={`categories-filter-button inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${
                    isActive ? "categories-filter-button--active" : ""
                  }`}
                >
                  {option.icon}
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
        {showFilterSpinner && (
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-[color:var(--ops-accent)]" />
            Updating results…
          </div>
        )}
      </section>
      {loading && categories.length === 0 ? (
        <CategoryGridSkeleton />
      ) : filteredCategories.length > 0 ? (
        <CategoryMarginsGrid applicationId={applicationId} categories={filteredCategories} />
      ) : categories.length === 0 ? (
        <CategoriesEmptyState categories={categories} />
      ) : (
        <NoResultsCard onReset={() => { setSearchTerm(""); setStatusFilter("all"); }} />
      )}
    </>
  );
}

const CategoriesEmptyState: React.FC<{ categories: ManufacturerCategory[] }> = ({ categories }) => (
  <Card className="categories-empty-card rounded-3xl border border-dashed shadow-sm">
    <CardHeader className="items-center space-y-3 text-center">
      <div className="categories-accent-pill inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide">
        <Sparkles className="h-4 w-4" /> No categories yet
      </div>
      <CardTitle className="text-3xl text-slate-900">Design your first assortment</CardTitle>
      <CardDescription className="text-base text-slate-600">
        Launch with confidence by adding a category. As soon as you publish, it becomes available to every reseller portal.
      </CardDescription>
    </CardHeader>
    <CardContent className="flex flex-col items-center gap-4 text-center">
      <CardDescription className="max-w-2xl text-base text-slate-600">
        Stand up your assortment in minutes and keep buyers in sync across every portal.
      </CardDescription>
      <CreateCategory
        categories={categories}
        renderTrigger={({ openDialog }) => (
          <Button onClick={openDialog} size="lg" className="w-full max-w-xs">
            <Plus className="mr-2 h-4 w-4" /> Create a category
          </Button>
        )}
      />
      <p className="text-xs text-slate-500">You can fine-tune automation and qualification later.</p>
    </CardContent>
  </Card>
);

const NoResultsCard: React.FC<{ onReset: () => void }> = ({ onReset }) => (
  <Card className="rounded-2xl border border-dashed border-rose-200 bg-rose-50/60">
    <CardHeader>
      <CardTitle>No categories match what you’re looking for</CardTitle>
      <CardDescription>
        Update your filters or clear search to see every category again.
      </CardDescription>
    </CardHeader>
    <CardContent className="flex flex-col gap-3 text-sm text-slate-600">
      <div className="inline-flex items-center gap-2 rounded-lg border border-rose-100 bg-white px-3 py-2">
        <AlertCircle className="h-4 w-4 text-rose-500" />
        Filters can be combined. Make sure you’re not limiting results unintentionally.
      </div>
      <Button onClick={onReset} className="self-start">
        Reset filters
      </Button>
    </CardContent>
  </Card>
);

const CategoryGridSkeleton = () => (
  <div className="grid gap-5 sm:grid-cols-2 2xl:grid-cols-3">
    {Array.from({ length: 6 }).map((_, index) => (
      <Card key={`category-skeleton-${index}`} className="rounded-2xl border border-slate-100 bg-white shadow-sm">
        <CardContent className="space-y-4 p-5">
          <div className="flex items-center gap-4">
            <Skeleton className="h-16 w-16 rounded-xl" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-8 w-8 rounded-full" />
          </div>
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
          <div className="grid gap-3 sm:grid-cols-3">
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
          </div>
          <Skeleton className="h-3 w-1/3" />
        </CardContent>
      </Card>
    ))}
  </div>
);

export default withRoot(Categories);
